import React, { useCallback, useEffect, useMemo, useState } from "react";
import Button, { ButtonType } from "../../components/Assets/Button";
import { useDispatch, useSelector } from "react-redux";
import OptimizedAddQuotationModal from "./OptimizedAddQuotationModal";
import toast from "react-hot-toast";
import {
  createLeadOrder,
  fetchLeadById,
  updateLeadData,
} from "../../store/slices/leadSlice";
import { RootDispatch, RootState } from "../../store";
import { useLocation, useParams } from "react-router-dom";
import { IProduct } from "../../models/productData.model";
import ILeadData, { IAddress } from "../../models/LeadData.model";
import { getStatesData } from "../../store/slices/kanbanSlice";
import {
  addQuotation,
  getAllQuotations,
  getCustomerQuotations,
  updateQuotation,
} from "../../store/slices/quotationSlice";
import OptimizedQuotationCard from "./OptimizedQuotationCard";
import { IOrderConfirmationData } from "../../models/epcCustomer.model";
import { CompanyInfo, IQuotationData } from "../../models/quotationData.model";
import { HandPrintAndDownload } from "../../components/Assets/generateBill/HandlePrintAndDownload";
import { calculateOrderTotals } from "../../utils/orderTotals";
import PaginationControls from "../../components/pagination/PaginationControls";

interface OptimizedQuotationManagerProps {
  from: "leadDetails" | "epcCustomer";
}

const OptimizedQuotationManager: React.FC<OptimizedQuotationManagerProps> = ({
  from,
}) => {
  const dispatch: RootDispatch = useDispatch();
  const [openModal, setOpenModal] = useState(false);
  const { leadId, customerId } = useParams<{
    leadId: string;
    customerId: string;
  }>();
  const location = useLocation();
  const card = location.state?.card;
  const companyName = card?.salesRepId?.companyName;

  const [isNextLoading, setIsNextLoading] = useState(false);
  const [isPrevLoading, setIsPrevLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [transportPrice, setTransportPrice] = useState<number>(0);
  const [discountPrice, setDiscountPrice] = useState<number>(0);
  const [name, setName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [mobileNumber, setMobileNumber] = useState<string>("");
  const [contactPersonName, setContactPersonName] = useState<string>("");
  const [address, setAddress] = useState<IAddress>({
    line1: "",
    city: "",
    state: "",
    pincode: "",
  });
  const [bookedProducts, setBookedProducts] = useState<IProduct[]>([]);

  const onClose = () => {
    setOpenModal(false);
    setBookedProducts([]);
    setIsEditMode(false);
    setEditingQuotation(null);
    setTransportPrice(0);
    setDiscountPrice(0);
  };

  const handleAddNewQuotation = () => {
    setIsEditMode(false);
    setEditingQuotation(null);
    setBookedProducts([]);
    setDiscountPrice(0);
    setTransportPrice(0);
    setOpenModal(true);
  };
  const [originalLead, setOriginalLead] = useState<ILeadData | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const isQuotationEmpty = useSelector(
    (state: RootState) => state.quotations.isQuotationEmpty,
  );
  const loading = useSelector((state: RootState) => state.quotations.loading);
  const [editingQuotation, setEditingQuotation] =
    useState<IQuotationData | null>(null);

  const lead = useSelector((state: RootState) => state.leadSlice.leads);
  const statesData = useSelector((state: RootState) => state.kanban.statesData);
  const quotations = useSelector(
    (state: RootState) => state.quotations.quotations,
  );
  const repaymentsTotalPages = useSelector(
    (state: RootState) => state.quotations.totalQuotations,
  );
  const QuotationCreatedBy = useSelector(
    (state: RootState) => state.auth.userName?.username,
  );
  const QuotationCreatedByNumber = useSelector(
    (state: RootState) => state.auth.mobileNumber,
  );
  const { isCreating, isUpdating } = useSelector(
    (state: RootState) => state.quotations,
  );
  const handleNextPage = async () => {
    if (currentPage >= repaymentsTotalPages) return;
    setIsNextLoading(true);
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    setIsNextLoading(false);
  };
  const handlePreviousPage = async () => {
    if (currentPage <= 1) return;
    setIsPrevLoading(true);
    const prevPage = currentPage - 1;
    setCurrentPage(prevPage);
    setIsPrevLoading(false);
  };

  useEffect(() => {
    if (leadId) {
      dispatch(fetchLeadById(leadId!)).unwrap();
    }
  }, [dispatch, leadId]);

  const fetchQuotations = useCallback(async () => {
    if (leadId) {
      if (from === "leadDetails") {
        dispatch(
          getAllQuotations({ leadId: leadId!, page: currentPage }),
        ).unwrap();
      } else {
        dispatch(getCustomerQuotations(customerId!)).unwrap();
      }
    }
  }, [currentPage, customerId, dispatch, from, leadId]);

  useEffect(() => {
    fetchQuotations();
  }, [fetchQuotations]);

  useEffect(() => {
    if (statesData.length === 0) {
      dispatch(getStatesData());
    }
  }, [dispatch, statesData.length]);

  useEffect(() => {
    if (lead) {
      setOriginalLead(lead);
      setName(lead.name);
      setEmail(lead.email || "");
      setContactPersonName(lead.contactPersonName || "");
      setMobileNumber(lead.mobileNumber || "");
      setAddress({
        line1: lead.address?.line1 || "",
        city: lead.address?.city || "",
        state: lead.address?.state || "",
        pincode: lead.address?.pincode || "",
      });
    }
  }, [lead]);

  useMemo(() => {
    if (!loading && isQuotationEmpty) {
      setOpenModal(true);
    } else {
      setOpenModal(false);
    }

    return quotations;
  }, [isQuotationEmpty, loading, quotations]);

  const getChangedFields = (): Partial<ILeadData> => {
    if (!originalLead) return {};

    const updates: Partial<ILeadData> = {};

    const originalName = originalLead.name || "";
    const originalEmail = originalLead.email || "";
    const originalMobile = originalLead.mobileNumber || "";
    const originalAddress = originalLead.address || {
      line1: "",
      city: "",
      state: "",
      pincode: "",
    };

    if (name.trim() !== originalName.trim()) updates.name = name.trim();
    if (email.trim() !== originalEmail.trim()) updates.email = email.trim();
    if (mobileNumber.trim() !== originalMobile.trim()) {
      updates.mobileNumber = mobileNumber.trim();
    }

    if (
      (address.line1 || "") !== (originalAddress.line1 || "") ||
      (address.city || "") !== (originalAddress.city || "") ||
      (address.state || "") !== (originalAddress.state || "") ||
      (address.pincode || "") !== (originalAddress.pincode || "") ||
      (address.district || "") !== (originalAddress.district || "")
    ) {
      updates.address = {
        line1: address.line1 || "",
        city: address.city || "",
        state: address.state || "",
        pincode: address.pincode || "",
        district: address.district || lead.address?.district,
      };
    }

    return updates;
  };

  const handleCreateQuotation = async (
    disableGst: () => void,
    customGst: number | null | undefined,
  ) => {
    if (isCreating) return;
    if (customGst !== null && customGst !== undefined && customGst <= 0) {
      toast.error("Custom GST cannot be 0");
      return;
    }
    if (!name.trim()) {
      toast.error("Name is required.");
      return;
    }

    const mobileRegex = /^\d{10}$/;
    if (!mobileRegex.test(mobileNumber)) {
      toast.error("Please enter a valid 10‑digit mobile number.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const trimmedEmail = email?.trim() || "";

    if (trimmedEmail && !emailRegex.test(trimmedEmail)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    if (!address.line1.trim()) {
      toast.error("Address line 1 is required.");
      return;
    }

    if (!address.state.trim()) {
      toast.error("State is required.");
      return;
    }

    if (!address.city.trim()) {
      toast.error("City is required.");
      return;
    }

    if (!address.pincode.trim()) {
      toast.error("Pincode is required.");
      return;
    }

    if (address.pincode && !/^\d{6}$/.test(address.pincode.trim())) {
      toast.error("Pincode must be exactly 6 digits.");
      return;
    }

    if (bookedProducts.length === 0) {
      toast.error("Please add at least one product.");
      return;
    }

    const totalRowDiscount = bookedProducts.reduce(
      (acc, p) => acc + (Number(p.discount) || 0),
      0,
    );

    if (totalRowDiscount > 0 && totalRowDiscount !== discountPrice) {
      toast.error(
        `Discount Mismatch: Row sum is ₹${totalRowDiscount} but Overall is ₹${discountPrice}. Please match them before creating.`,
      );
      return;
    }

    try {
      const changed = getChangedFields();

      if (!leadId) {
        toast.error("Lead ID is missing.");
        return;
      }
      const newQuotation: IQuotationData = await dispatch(
        addQuotation({
          leadId,
          bookedProducts,
          transportPrice,
          discountPrice,
          customGst,
        }),
      ).unwrap();

      const companyInfo: CompanyInfo = {
        name: companyName?.trim() || name.trim(),
        email,
        contactPersonName: contactPersonName.trim(),
        mobileNumber,
        billingAddress: address.line1,
        billingCity: address.city,
        billingState: address.state,
        billingPincode: address.pincode,
        quotationNumber: newQuotation.quotationNumber || "",
        quotationDate: newQuotation.createdAt,
        quotationCreatedBy: QuotationCreatedBy,
        QuotationCreatedByNumber: QuotationCreatedByNumber,
      };

      const productsForPdf = newQuotation.items.map((item: any) => ({
        _id: item.product || item.rawMaterial,
        productName: item.productName || "Item",
        quantity: item.quantity,
        price: item.price ?? 0,
        gst: customGst ?? item.gst ?? 0,
        hsn: item.hsn ?? 0,
        unit: item.unit,
        type: item.type,
        discount: item.discount || 0,
      }));

      HandPrintAndDownload(
        productsForPdf,
        companyInfo,
        transportPrice,
        discountPrice,
      );

      if (Object.keys(changed).length > 0) {
        await dispatch(
          updateLeadData({
            _id: leadId!,
            data: changed,
          }),
        ).unwrap();
      }
      disableGst();
      onClose();
    } catch (err: any) {
      toast.error(err.message || "Failed to create quotation");
      console.error(err);
    }
  };

  const handleEditQuotation = (quote: IQuotationData) => {
    setEditingQuotation(quote);
    setTransportPrice(quote.transportPrice || 0);
    setDiscountPrice(quote.discountPrice || 0);

    setBookedProducts(
      quote.items.map((item) => {
        const isMaterial = item.type === "material";
        const detail = isMaterial ? item.rawMaterial : item.product;
        const targetId =
          typeof detail === "object" && detail !== null ? detail._id : detail;

        return {
          _id: targetId || item._id,
          productName: detail?.productName || detail?.name || "",
          quantity: item.quantity,
          price: item.price ?? 0,
          gst: item.gst ?? 0,
          hsn: item.hsn ?? 0,
          unit: item.unit || detail?.unit || detail?.unitType || "",
          unitType: item.unitType || detail?.unitType || detail?.unit || "",
          type: item.type || "product",
          msp: 0,
          discount: item.discount || 0,
        };
      }),
    );

    setIsEditMode(true);
    setOpenModal(true);
  };

  const handleUpdateQuotation = async (
    disableGst: () => void,
    customGst: number | null | undefined,
  ) => {
    if (isUpdating || !editingQuotation) return;
    console.log("customGst", customGst);
    const totalRowDiscount = bookedProducts.reduce(
      (acc, p) => acc + (Number(p.discount) || 0),
      0,
    );

    if (totalRowDiscount > 0 && totalRowDiscount !== discountPrice) {
      toast.error(
        `Discount Mismatch: Row sum is ₹${totalRowDiscount} but Overall is ₹${discountPrice}.`,
      );
      return;
    }

    try {
      const productsPayload = bookedProducts.map((p) => ({
        _id: p._id,
        productName: p.productName,
        quantity: p.quantity,
        price: p.price,
        unit: p.unit,
        type: p.type,
        gst: p.gst,
        hsn: p.hsn,
        unitType: p.unitType,
        msp: p.msp ?? 0,
        discount: p.discount || 0,
      }));

      await dispatch(
        updateQuotation({
          leadId: leadId!,
          quotationId: editingQuotation._id,
          products: productsPayload,
          transportPrice,
          discountPrice,
          oldQuotation: editingQuotation,
        }),
      ).unwrap();

      await fetchQuotations();

      setOpenModal(false);
      setIsEditMode(false);
      setEditingQuotation(null);
    } catch (error: any) {
      toast.error(error.message || "Failed to update quotation");
      console.error("Update Error:", error);
    }
  };

  const handleConfirmQuotation = async (
    quotationId: string,
    orderData: IOrderConfirmationData,
  ): Promise<string> => {
    try {
      const quotation = quotations.find((q) => q._id === quotationId);
      if (!quotation) {
        throw new Error("Quotation not found");
      }

      const selectedGoods = quotation.items.map((item) => {
        const isMaterial = item.type === "material";
        const detail = isMaterial ? item.rawMaterial : item.product;

        const id =
          (typeof detail === "object" && detail !== null
            ? detail._id
            : detail) || item._id;
        const label =
          item.productName ||
          (isMaterial ? (detail as any)?.name : (detail as any)?.productName) ||
          "Unknown Item";

        return {
          _id: id,
          label: label || "Unknown Item",
          quantity: item.quantity,
          price: item.price,
          gst: item.gst || 0,
          hsn: item.hsn || 0,
          type: item.type,
          unit: item.unit,
          discount: item.discount || 0,
        };
      });

      const orderItems = selectedGoods.map((item) => ({
        productName: item.label,
        price: item.price!,
        quantity: item.quantity,
        transportPrice: orderData.transportPrice,
        discountPrice: orderData.discountPrice,
      }));

      const customerOrders = selectedGoods.map((item) => ({
        _id: item._id,
        productName: item.label,
        hsn: item.hsn,
        price: item.price ?? 0,
        gst: item.gst,
        productId: item._id,
        quantity: item.quantity,
        discount: item.discount ?? 0,
        transportPrice: orderData.transportPrice,
        discountPrice: orderData.discountPrice,
      }));

      const { grandTotal } = calculateOrderTotals(orderItems, customerOrders);

      const orderResponse = await dispatch(
        createLeadOrder({
          category: lead?.category || "",
          name: orderData.name,
          mobileNumber: orderData.mobileNumber,
          email: orderData.email,
          shippingAddress: orderData.shippingAddress,
          billingAddress: orderData.billingAddress,
          leadId: leadId!,
          selectedGoods,
          gstin: orderData.gstin,
          quotationId,
          transportPrice: orderData.transportPrice,
          discountPrice: orderData.discountPrice,
          grandTotal,
        }),
      ).unwrap();

      await fetchQuotations();

      toast.success("Order created successfully!");
      return orderResponse.orderNumber;
    } catch (error: any) {
      toast.error(error.message || "Failed to create order");
      throw error;
    }
  };

  return (
    <div>
      <div className="p-4">
        <Button
          type={ButtonType.Submit}
          onClick={handleAddNewQuotation}
          className="text-xs"
        >
          Add Quotation
        </Button>
      </div>

      <OptimizedAddQuotationModal
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        name={name}
        setName={setName}
        email={email}
        setEmail={setEmail}
        mobileNumber={mobileNumber}
        setMobileNumber={setMobileNumber}
        address={address}
        setAddress={setAddress}
        transportPrice={transportPrice}
        discountPrice={discountPrice}
        setTransportPrice={setTransportPrice}
        setDiscountPrice={setDiscountPrice}
        bookedProducts={bookedProducts}
        setBookedProducts={setBookedProducts}
        handleCreateQuotation={handleCreateQuotation}
        statesData={statesData}
        isEdit={isEditMode}
        handleUpdateQuotation={handleUpdateQuotation}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 p-2">
        {quotations.map((quotation) => (
          <OptimizedQuotationCard
            key={quotation._id}
            quotation={quotation}
            onConfirm={handleConfirmQuotation}
            lead={lead}
            statesData={statesData}
            onEdit={handleEditQuotation}
          />
        ))}
      </div>
      <div className="p-4">
        <PaginationControls
          isNextFetching={isNextLoading}
          isPrevFetching={isPrevLoading}
          currentPage={currentPage}
          totalPages={repaymentsTotalPages}
          onNext={handleNextPage}
          onPrev={handlePreviousPage}
        />
      </div>
    </div>
  );
};

export default OptimizedQuotationManager;
